import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import { emit } from '../socket';

const USE_CASES = [
  { key: "fitness", icon: "🏋️", label: "Fitness", desc: "Track workouts, reps, and exercises" },
  { key: "health", icon: "❤️", label: "Health", desc: "Monitor medications, symptoms, habits" },
  { key: "cooking", icon: "🍳", label: "Cooking", desc: "Time recipes and cooking steps" },
  { key: "baby", icon: "👶", label: "Baby", desc: "Feeding, diaper changes, sleep" },
  { key: "work", icon: "💼", label: "Work", desc: "Time tasks, meetings, focus sessions" },
  { key: "fishing", icon: "🎣", label: "Fishing", desc: "Track catches, bait changes, spots" },
  { key: "leisure", icon: "🎮", label: "Leisure", desc: "Gaming, reading, hobbies" },
  { key: "custom", icon: "⚡", label: "Custom", desc: "Start from scratch" },
];

function RegisterPage() {
  const [mode, setMode] = useState('quick');
  const [step, setStep] = useState(1);
  const [deviceId, setDeviceId] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Quick start state
  const [quickName, setQuickName] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState(null);

  // Virtual device state
  const [vdName, setVdName] = useState('');
  const [vdEmail, setVdEmail] = useState('');
  const [vdReason, setVdReason] = useState('');
  const [vdSubmitted, setVdSubmitted] = useState(false);
  const [vdRequestId, setVdRequestId] = useState(null);
  const [vdStatus, setVdStatus] = useState(null);

  document.getElementsByTagName('body')[0].className = 'page-sidebar-hidden';

  const handleQuickStart = async (e) => {
    e.preventDefault();
    setError(null);
    if (!quickName.trim() || quickName.trim().length < 2) {
      setError('Please enter your name (at least 2 characters).');
      return;
    }
    setLoading(true);
    try {
      await AuthService.quickRegister(quickName.trim());
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDevice = async (e) => {
    e.preventDefault();
    setError(null);
    if (!deviceId.trim()) {
      setError('Please enter a Device ID.');
      return;
    }
    setLoading(true);
    try {
      const result = await AuthService.checkDevice(deviceId.trim());
      if (!result.exists) {
        setError('Device ID not found. Please check your device.');
      } else if (result.registered) {
        setError('This device is already registered. Please login instead.');
      } else {
        setStep(2);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    if (pin.length < 4) {
      setError('PIN must be at least 4 characters.');
      return;
    }
    if (pin !== confirmPin) {
      setError('PINs do not match.');
      return;
    }
    setLoading(true);
    try {
      await AuthService.register(deviceId.trim(), pin);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVirtualDeviceRequest = async (e) => {
    e.preventDefault();
    setError(null);
    if (!vdName.trim() || vdName.trim().length < 2) {
      setError('Please enter your name (at least 2 characters).');
      return;
    }
    setLoading(true);
    try {
      const res = await emit('auth:requestVirtualDevice', {
        requesterName: vdName.trim(),
        email: vdEmail.trim() || undefined,
        reason: vdReason.trim() || undefined,
      });
      setVdRequestId(res.data.id);
      setVdSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!vdRequestId) return;
    setError(null);
    setLoading(true);
    try {
      const res = await emit('auth:checkVirtualDeviceRequest', { requestId: vdRequestId });
      setVdStatus(res.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="container">
        <div className="row justify-content-md-center">
          <div className="col-md-12 col-lg-6">
            <div className="card login-box-container">
              <div className="card-body">
                <div className="authent-logo">
                  <img src={'./logo-kronix.png'} alt="Kronix Logo" style={{ maxWidth: '100%' }} />
                </div>

                {/* Mode Toggle */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderRadius: 12, overflow: 'hidden', border: '0.5px solid rgba(255,255,255,0.12)' }}>
                  <button
                    type="button"
                    onClick={() => { setMode('quick'); setError(null); }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      background: mode === 'quick' ? 'rgba(48,209,88,0.15)' : 'transparent',
                      color: mode === 'quick' ? '#30D158' : 'rgba(255,255,255,0.4)',
                      border: 'none',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    Quick Start
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('device'); setError(null); }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      background: mode === 'device' ? 'rgba(10,132,255,0.2)' : 'transparent',
                      color: mode === 'device' ? '#0A84FF' : 'rgba(255,255,255,0.4)',
                      border: 'none',
                      borderLeft: '0.5px solid rgba(255,255,255,0.12)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    I Have a Device
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('virtual'); setError(null); }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      background: mode === 'virtual' ? 'rgba(10,132,255,0.2)' : 'transparent',
                      color: mode === 'virtual' ? '#0A84FF' : 'rgba(255,255,255,0.4)',
                      border: 'none',
                      borderLeft: '0.5px solid rgba(255,255,255,0.12)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    Virtual Device
                  </button>
                </div>

                {/* Quick Start Mode */}
                {mode === 'quick' && (
                  <>
                    <div className="authent-text">
                      <p>Start tracking in seconds — no hardware needed.</p>
                    </div>

                    <form onSubmit={handleQuickStart}>
                      <div className="mb-3">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id="quickName"
                            placeholder="Your Name"
                            value={quickName}
                            onChange={(e) => setQuickName(e.target.value)}
                          />
                          <label htmlFor="quickName">Your Name</label>
                        </div>
                      </div>

                      {/* Use Case Picker */}
                      <div style={{ marginBottom: 16 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                          What do you want to track?
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
                          {USE_CASES.map((uc) => (
                            <button
                              key={uc.key}
                              type="button"
                              className={`use-case-btn ${selectedUseCase === uc.key ? 'use-case-btn-active' : ''}`}
                              onClick={() => setSelectedUseCase(uc.key === selectedUseCase ? null : uc.key)}
                            >
                              <span style={{ fontSize: 22, display: 'block', marginBottom: 4 }}>{uc.icon}</span>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>{uc.label}</span>
                            </button>
                          ))}
                        </div>
                        {selectedUseCase && (
                          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 8, textAlign: 'center' }}>
                            {USE_CASES.find((u) => u.key === selectedUseCase)?.desc}
                          </p>
                        )}
                      </div>

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-success m-b-xs"
                          disabled={loading}
                          style={{ padding: '12px 20px', fontSize: 16, fontWeight: 700 }}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm" style={{ marginRight: 8 }}></span>
                              Setting up...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-rocket" style={{ marginRight: 8 }}></i>
                              Start Tracking Now
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </>
                )}

                {/* Device Mode */}
                {mode === 'device' && (
                  <>
                    <div className="authent-text">
                      <p>{step === 1 ? 'Register your hardware device' : 'Set your PIN'}</p>
                    </div>

                    {step === 1 ? (
                      <form onSubmit={handleCheckDevice}>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="text"
                              className="form-control"
                              id="floatingDeviceId"
                              placeholder="Device ID"
                              value={deviceId}
                              onChange={(e) => setDeviceId(e.target.value)}
                            />
                            <label htmlFor="floatingDeviceId">Device ID</label>
                          </div>
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-info m-b-xs" disabled={loading}>
                            {loading ? 'Checking...' : 'Continue'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <form onSubmit={handleRegister}>
                        <div className="mb-3">
                          <div className="alert alert-success" role="alert">
                            Device <strong>{deviceId}</strong> found!
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="password"
                              className="form-control"
                              id="floatingPin"
                              placeholder="PIN"
                              value={pin}
                              onChange={(e) => setPin(e.target.value)}
                            />
                            <label htmlFor="floatingPin">PIN (min 4 characters)</label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="password"
                              className="form-control"
                              id="floatingConfirmPin"
                              placeholder="Confirm PIN"
                              value={confirmPin}
                              onChange={(e) => setConfirmPin(e.target.value)}
                            />
                            <label htmlFor="floatingConfirmPin">Confirm PIN</label>
                          </div>
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-info m-b-xs" disabled={loading}>
                            {loading ? 'Registering...' : 'Register'}
                          </button>
                        </div>
                        <div className="d-grid mt-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => { setStep(1); setError(null); setPin(''); setConfirmPin(''); }}
                          >
                            Back
                          </button>
                        </div>
                      </form>
                    )}
                  </>
                )}

                {/* Virtual Device Mode */}
                {mode === 'virtual' && (
                  <>
                    <div className="authent-text">
                      <p>Request a virtual device to use Kronix without hardware</p>
                    </div>

                    {!vdSubmitted ? (
                      <form onSubmit={handleVirtualDeviceRequest}>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="text"
                              className="form-control"
                              id="vdName"
                              placeholder="Your Name"
                              value={vdName}
                              onChange={(e) => setVdName(e.target.value)}
                            />
                            <label htmlFor="vdName">Your Name</label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="email"
                              className="form-control"
                              id="vdEmail"
                              placeholder="Email (optional)"
                              value={vdEmail}
                              onChange={(e) => setVdEmail(e.target.value)}
                            />
                            <label htmlFor="vdEmail">Email (optional)</label>
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="form-floating">
                            <input
                              type="text"
                              className="form-control"
                              id="vdReason"
                              placeholder="Reason (optional)"
                              value={vdReason}
                              onChange={(e) => setVdReason(e.target.value)}
                            />
                            <label htmlFor="vdReason">Reason (optional)</label>
                          </div>
                        </div>
                        <div className="d-grid">
                          <button type="submit" className="btn btn-info m-b-xs" disabled={loading}>
                            {loading ? 'Submitting...' : 'Submit Request'}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div>
                        <div className="alert alert-success" role="alert" style={{ marginBottom: 16 }}>
                          Request submitted! An admin will review it shortly.
                        </div>

                        {vdStatus && (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{
                              background: 'rgba(255,255,255,0.06)',
                              border: '0.5px solid rgba(255,255,255,0.12)',
                              borderRadius: 12,
                              padding: 16,
                            }}>
                              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 8 }}>
                                Status
                              </div>
                              <div style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: vdStatus.status === 'approved' ? '#30D158' : vdStatus.status === 'rejected' ? '#FF453A' : '#FFD60A',
                                marginBottom: vdStatus.assignedDeviceId ? 12 : 0,
                                textTransform: 'uppercase',
                              }}>
                                {vdStatus.status}
                              </div>
                              {vdStatus.assignedDeviceId && (
                                <div>
                                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>Your Virtual Device ID:</div>
                                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0A84FF', fontFamily: 'monospace' }}>
                                    {vdStatus.assignedDeviceId}
                                  </div>
                                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
                                    Use this ID to register on the Hardware Device tab.
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="d-grid">
                          <button
                            type="button"
                            className="btn btn-info m-b-xs"
                            onClick={handleCheckStatus}
                            disabled={loading}
                          >
                            {loading ? 'Checking...' : 'Check Status'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {error && (
                  <div className="authent-text" style={{ color: '#FF453A', marginTop: '10px' }}>
                    {error}
                  </div>
                )}

                <div className="authent-text" style={{ marginTop: '16px' }}>
                  <p>Already registered? <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Sign In</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
